import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Card from '../ui/Card';
import { useAnalytics } from '../../context/AnalyticsContext';
import { CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';

interface OAuthCallbackHandlerProps {
  provider: 'zoho' | 'powerbi';
}

const OAuthCallbackHandler: React.FC<OAuthCallbackHandlerProps> = ({ provider }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { actions } = useAnalytics();
  
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Processing OAuth callback...');
  const [details, setDetails] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const code = searchParams.get('code');
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');
        const success = searchParams.get('success');
        const state = searchParams.get('state');

        if (error) {
          setStatus('error');
          setMessage(`OAuth Error: ${error}`);
          setDetails(errorDescription || 'Authorization was denied or failed.');
          return;
        }

        if (success === 'true') {
          setStatus('success');
          setMessage('Authorization successful!');
          setDetails('You can now use analytics features.');
          
          // Check credential status to update state
          await actions.checkProviderStatus(provider);
          
          // Redirect back to analytics page after a short delay
          setTimeout(() => {
            navigate('/analytics');
          }, 2000);
          return;
        }

        if (success === 'false' || error) {
          setStatus('error');
          setMessage('Authorization failed');
          setDetails('Please try the setup process again.');
          return;
        }

        if (code) {
          setMessage('Exchanging authorization code...');
          
          // The actual token exchange should be handled by the backend
          // through the redirect flow, but we can update our local state
          await actions.checkProviderStatus(provider);
          
          setStatus('success');
          setMessage('Authorization successful!');
          setDetails('Credentials have been stored and verified.');
          
          // Redirect back to analytics page
          setTimeout(() => {
            navigate('/analytics');
          }, 2000);
        } else {
          setStatus('error');
          setMessage('Missing authorization code');
          setDetails('The OAuth flow did not complete properly. Please try again.');
        }
      } catch (error: any) {
        console.error('OAuth callback error:', error);
        setStatus('error');
        setMessage('Failed to process OAuth callback');
        setDetails(error.message || 'An unexpected error occurred.');
      }
    };

    handleCallback();
  }, [searchParams, provider, actions, navigate]);

  const providerName = provider === 'zoho' ? 'Zoho Analytics' : 'Power BI';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Card className="p-8 max-w-md w-full mx-4">
        <div className="text-center">
          <div className="mb-6">
            {status === 'processing' && (
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
              </div>
            )}
            {status === 'success' && (
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
            )}
            {status === 'error' && (
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
            )}
          </div>

          <h2 className="text-xl font-bold text-text-primary mb-2">
            {providerName} Authorization
          </h2>
          
          <p className="text-text-primary font-medium mb-2">
            {message}
          </p>
          
          {details && (
            <p className="text-sm text-text-secondary mb-4">
              {details}
            </p>
          )}

          {status === 'processing' && (
            <div className="text-sm text-text-secondary">
              Please wait while we complete the authorization process...
            </div>
          )}

          {status === 'success' && (
            <div className="text-sm text-green-600">
              Redirecting you back to the analytics page...
            </div>
          )}

          {status === 'error' && (
            <div className="mt-4">
              <button
                onClick={() => navigate('/analytics')}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Back to Analytics
              </button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default OAuthCallbackHandler;